import {useQuery} from '@tanstack/react-query';
import {selectUserAccounts} from "../api/user/UserAccountService";
import {selectServiceCodeForDrawer} from "../api/serviceCode/ServiceCodeService";
import {getProjectCodeListBySearch} from "../api/projectcode/ProjectCodeService";
import {getCodes, selectCodesByParentId} from "../api/code/CodeService";
import {getDepartments} from "../packages/settings/department/DepartmentService";
import {CodeSpecNum, ContentCategory} from "../constant/CodeSpecNum";
import {getCompanyList} from "../api/company/CompanyService";
import {getCustomCodeList} from "../api/field/CustomCodeService";
import {useEffect, useState} from "react";
import {selectUserGroupsAsList} from "../api/usergroup/UserGroupService";
import {getDailyCheckList} from "../api/dailyCheck/DailyCheckService";
import {
    getAssetMaintenanceCompanies,
    getAssets,
    getAssetsDispose,
    getAssetsLifeCycle,
    getAssetsNotUsed
} from "../api/asset/AssetService";
import {selectApprovalProcesses} from "../api/approval/ApprovalProcessService";
import {selectApprovalGroups} from "../api/approval/ApprovalGroupService";
import {getContractList} from "../api/asset/AssetContractService";
import {
    getInspectionReportCodeUsedAsset,
    getInspectionReportCodeUsedCompany
} from "../api/inspectionReportCode/InspectionReportCodeService";
import {selectServiceRequest} from "../api/serviceRequest/ServiceRequestService";
import {getIncidentList} from "../api/maintenance/IncidentService";
import {getInspectionList} from "../api/maintenance/InspectionService";
import {getAssetsForDiligence} from "../api/asset/DiligenceService";
import {getRentalAssetsForDrawer} from "../api/lifecycle/rental/rentalAssetService";

/**
 *
 * 별도의 페이징, 검색조건을 먹이고 싶으면 호출하는 곳에서 uniqueKey 를 넣어줘야 함.
 * */
const useCodeData = (target, uniqueKey = null) => {
    const [searchConditions, setSearchConditions] = useState({
        searchTxt: '',
    });

    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        size: 20,
        sort: 'createdAt',
        desc: true
    });

    const fetchData = async (conditions, page, size, sort, desc) => {
        const pageable = { page, size, sort, desc };
        let response;
        switch (target) {
            case 'code':
                response = await getCodes();
                break;
            case 'project':
                // response = await getProjectCodeList();
                response = await getProjectCodeListBySearch(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'location':
                response = await selectCodesByParentId(CodeSpecNum.AssetLocation);
                break;
            case 'serviceCode':
                response = await selectServiceCodeForDrawer(CodeSpecNum.ServiceRequestGroupCode);
                break;
            case 'userAccount':
                response = await selectUserAccounts(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'department':
                response = await getDepartments();
                break;
            case 'inspectionCode':
                response = await selectCodesByParentId(CodeSpecNum.InspectionRequestCode);
                break;
            case 'incidentCode':
                response = await selectCodesByParentId(CodeSpecNum.IncidentRequestCode);
                break;
            case 'workCode':
                response = await selectCodesByParentId(CodeSpecNum.WorkRequestCode);
                break;
            case 'introductionCode':
                response = await selectCodesByParentId(CodeSpecNum.IntroductionRequestCode);
                break;
            case 'disposeCode':
                response = await selectCodesByParentId(CodeSpecNum.DisposeRequestCode);
                break;
            // case 'company':
            //     response = await getTopCompanylist();
            //     break;
            case 'company':
                response = await getCompanyList(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'asset':
                response = await getAssets(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'asset-diligence':
                response = await getAssetsForDiligence(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'depreciationAsset':
                response = await getAssetsNotUsed(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'maintenance-assets':
                response = await getAssetMaintenanceCompanies(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'customCode':
                response = await getCustomCodeList();
                break;
            case 'userGroup':
                response = await selectUserGroupsAsList(conditions);
                break;
            case 'dailyCheck':
                response = await getDailyCheckList(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'approvalProcess':
                response = await selectApprovalProcesses(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'approvalProcessAfter':
                response = await selectApprovalProcesses(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'approvalGroup':
                response = await selectApprovalGroups(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'rental':
                response = await getAssetsLifeCycle(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'dispose':
                response = await getAssetsDispose(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'contract':
                response = await getContractList(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'inspectionReportAsset':
                response = await getInspectionReportCodeUsedAsset(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case 'inspectionReportCompany':
                response = await getInspectionReportCodeUsedCompany(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case ContentCategory.SERVICE_REQUEST:
                response = await selectServiceRequest(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case ContentCategory.INCIDENT:
                response = await getIncidentList(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case ContentCategory.INSPECTION:
                response = await getInspectionList(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            case ContentCategory.RENTAL_ASSET:
                response = await getRentalAssetsForDrawer(conditions, pageable);
                setPageInfo({
                    currentPage: response.data.pageable.pageNumber,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                    size: size,
                    sort: sort,
                    desc: desc
                });
                break;
            default:
                throw new Error(`Unknown target: ${target}`);
        }

        if (response && response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected response status: ${response?.status}`);
        }
    };

    // `queryKey`를 고유하게 설정
    const { data, error, isLoading, refetch } = useQuery({
        queryKey: uniqueKey ? ['fetchData', target, uniqueKey, pageInfo, searchConditions] : ['fetchData', target, pageInfo, searchConditions],
        queryFn: () => fetchData(searchConditions, pageInfo.currentPage, pageInfo.size, pageInfo.sort, pageInfo.desc),
        // 유지시간은 2분설정
        staleTime: 1000 * 60 * 2,
    });

    useEffect(() => {
        if (data) {
            setPageInfo(prev => ({
                ...prev,
                totalPages:    data.totalPages,
                totalElements: data.totalElements,
            }));
        }
    }, [data]);

    const handleSearch = (newConditions) => {
        const updatedConditions = Object.keys(newConditions ?? {}).length === 0
            ? Object.keys(searchConditions).reduce((acc, key) => {
                if (typeof searchConditions[key] !== "object" || searchConditions[key] === null) {
                    acc[key] = searchConditions[key];
                }
                return acc;
            }, {})
            : {...newConditions};

        setSearchConditions(updatedConditions);
        setPageInfo((prev) => ({...prev, currentPage: 0}));
    };

    const handlePageChange = (newPage) => {
        setPageInfo((prev) => ({
            ...prev,
            currentPage: newPage
        }));
    };

    return { dataSet: data, isLoading, error, searchConditions, pageInfo, handleSearch, handlePageChange, refetch };
};

export default useCodeData;
